import React, { useRef, useState } from 'react';
import { Upload, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadFile } from '../../services/storage/uploadService';
import { sanitizeFileName, validateFileType } from '../../utils/fileUtils';

interface FileUploadProps {
  bucket: string;
  path: string;
  onUpload: (url: string) => void;
  accept?: string;
  className?: string;
  maxSize?: number;
}

export function FileUpload({ bucket, path, onUpload, accept = 'image/*', className = '', maxSize = 5 * 1024 * 1024 }: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Reset file input value to allow uploading same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Reset file input value to allow uploading same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Reset file input value to allow uploading same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      // Validate file size
      if (file.size > maxSize) {
        throw new Error(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      }

      // Validate file type
      if (!validateFileType(file, accept)) {
        throw new Error(`Please upload a ${accept.replace(/\./g, '').toUpperCase()} file`);
      }

      // Create sanitized file with allowed characters
      const sanitizedName = sanitizeFileName(file.name);
      const sanitizedFile = new File([file], sanitizedName, { type: file.type });

      setIsUploading(true);
      const { url } = await uploadFile(
        sanitizedFile,
        bucket,
        path ? `${path}/${sanitizedName}` : sanitizedName
      );
      onUpload(url);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      let message = 'Failed to upload file';
      if (error instanceof Error) {
        // Handle specific error cases
        if (error.message.includes('storage quota')) {
          message = 'Storage quota exceeded. Please contact support.';
        } else if (error.message.includes('permission denied')) {
          message = 'You do not have permission to upload files.';
        } else {
          message = error.message;
        }
      }
      toast.error(message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="w-full border-2 border-dashed border-gray-700 rounded-xl p-6 hover:border-accent-green transition-colors"
      >
        {isUploading ? (
          <div className="flex flex-col items-center">
            <Loader className="h-8 w-8 text-accent-green animate-spin" />
            <span className="mt-2 text-sm text-gray-400">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="h-8 w-8 text-gray-400" />
            <span className="mt-2 text-sm text-gray-400">Click to upload file</span>
          </div>
        )}
      </button>
    </div>
  );
}